import { useSelector } from 'react-redux'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CountUp } from 'countup.js'
import { renderValue } from '@utils/numberUtils'
import { WalletSVG } from '@media/svgs'

const BalanceModule = () => {
  const { balance, displayCurrencyCode } = useSelector((state) => state.wallet)
  const { number, currency, currencyConfig } = useSelector((state) => state.conventions)
  const [balanceChange, setBalanceChange] = useState({ active: false, sign: '' })

  const decimalPrecision = currencyConfig?.balanceOutputPrecision?.max ?? 2
  const balanceRef = useRef(null)
  const previousBalance = useRef(balance || 0)
  const flashTimeoutRef = useRef(null)

  const formatBalanceValue = useCallback(
    (value) =>
      renderValue({
        value,
        conventions: { number, currency, currencyConfig },
        options: { precisionType: 'balance' },
      }),
    [currency, currencyConfig, number]
  )

  useEffect(() => {
    if (previousBalance.current !== balance) {
      const diff = balance - previousBalance.current

      if (diff !== 0) {
        setBalanceChange({ active: true, sign: diff > 0 ? '+' : '-' })

        clearTimeout(flashTimeoutRef.current)
        flashTimeoutRef.current = setTimeout(() => {
          setBalanceChange({ active: false, sign: '' })
        }, 1000)
      }

      const counter = new CountUp(balanceRef.current, balance, {
        startVal: previousBalance.current,
        duration: 1,
        useEasing: true,
        decimalPlaces: decimalPrecision,
        formattingFn: formatBalanceValue,
      })

      if (!counter.error) {
        counter.start()
      }

      previousBalance.current = balance
    }
  }, [balance, decimalPrecision, formatBalanceValue])

  useEffect(() => () => clearTimeout(flashTimeoutRef.current), [])

  const changeClass = balanceChange.active
    ? balanceChange.sign === '+'
      ? 'standard-normal-positive-change'
      : 'standard-normal-negative-change'
    : ''

  return (
    <div className="standard-normal-balance-module-container">
      <div className="standard-normal-balance-module-icon-container">
        <WalletSVG />
      </div>
      <div className="standard-normal-balance-module-balance-container">
        <div className={`standard-normal-balance-module-currency-box ${changeClass}`}>
          {balanceChange.active ? `${balanceChange.sign}${displayCurrencyCode}` : displayCurrencyCode}
        </div>
        <div ref={balanceRef} className={`standard-normal-balance-module-amount-box ${changeClass}`}>
          {formatBalanceValue(balance)}
        </div>
      </div>
    </div>
  )
}

export default BalanceModule
